import { Component } from "react";

//Un rechargement automatique au maximum par session, pour ne jamais boucler
const CLE_RECHARGEMENT = "rechargement_apres_erreur_de_chunk";

/**
 * Vrai lorsque l'erreur vient d'un fragment de code introuvable.
 *
 * Cas typique : une nouvelle version est déployée pendant qu'une session est ouverte.
 * Les fichiers portent alors un nouveau nom et les pages chargées à la demande
 * (lazy) ne peuvent plus être téléchargées.
 */
function estErreurDeChunk(error) {
  if (!error) {
    return false;
  }

  const message = `${error.name || ""} ${error.message || ""}`;

  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed/i.test(
    message
  );
}

//sessionStorage peut être indisponible (navigation privée, cookies bloqués)
function lireIndicateurRechargement() {
  try {
    return sessionStorage.getItem(CLE_RECHARGEMENT) === "1";
  } catch (e) {
    return true; //En cas de doute, on ne recharge pas automatiquement
  }
}

function marquerRechargement() {
  try {
    sessionStorage.setItem(CLE_RECHARGEMENT, "1");
  } catch (e) {
    //Sans stockage disponible, le rechargement reste manuel
  }
}

/**
 * Filet de sécurité autour du routeur.
 *
 * Sans lui, une page chargée à la demande dont le fragment est introuvable fait
 * disparaître toute l'interface : React démonte l'arbre et l'écran reste blanc.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, estChunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, estChunk: estErreurDeChunk(error) };
  }

  componentDidCatch(error, errorInfo) {
    //La trace reste disponible pour le diagnostic
    console.error("Erreur non rattrapée :", error, errorInfo);

    //Nouvelle version déployée : un seul rechargement suffit à récupérer les fichiers
    if (estErreurDeChunk(error) && !lireIndicateurRechargement()) {
      marquerRechargement();
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main id="main" className="main">
        <div className="app-content pt-3 p-md-3 p-lg-4">
          <div className="container-xl">
            <div className="app-card app-card-settings shadow-lg mb-5">
              <div className="app-card-body p-4 p-lg-5 text-center">
                <i
                  className="bi bi-exclamation-triangle text-warning"
                  style={{ fontSize: "3rem" }}
                ></i>

                <h3 className="app-page-title mt-3 mb-3">
                  {this.state.estChunk
                    ? "Une nouvelle version est disponible"
                    : "Une erreur est survenue"}
                </h3>

                <p className="text-muted mb-4">
                  {this.state.estChunk ? (
                    <>
                      L'application a été mise à jour depuis l'ouverture de
                      votre session. Rechargez la page pour récupérer la
                      dernière version.
                    </>
                  ) : (
                    <>
                      L'affichage de cette page a été interrompu. Rechargez la
                      page, puis signalez le problème s'il persiste.
                    </>
                  )}
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => window.location.reload()}
                  >
                    <i className="bi bi-arrow-clockwise"></i>&nbsp;Recharger la
                    page
                  </button>
                  <a className="btn btn-secondary" href="/">
                    <i className="bi bi-house-door"></i>&nbsp;Accueil
                  </a>
                </div>
              </div>
              {/*//app-card-body*/}
            </div>
            {/*//app-card*/}
          </div>
          {/*//container-xl*/}
        </div>
        {/*//app-content*/}
      </main>
    );
  }
}
